import {Flag, IconButton, Tooltip} from 'platform/components';
import {HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {GetOrderItemForMaterialApiResponse} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';

import {composePath, suffixTestId, TestIdProps} from 'shared/web';

interface CoreExchangeBadgeProps extends TestIdProps {
  coreExchangeId: string;
  coreExchangeItemType: NonNullable<GetOrderItemForMaterialApiResponse>['coreExchangeItemType'];
}

/**
 * Marks a service order material item that belongs to a core exchange, says whether it is the
 * MAIN (new) or the CORE (returned) item and opens the core exchange detail in a new tab.
 */
export function CoreExchangeBadge(props: CoreExchangeBadgeProps) {
  const itemTypeLabel = match(props.coreExchangeItemType)
    .with('MAIN', () => i18n.t('entity.coreExchange.labels.mainItemNew'))
    .with('CORE', () => i18n.t('entity.coreExchange.labels.coreItemReturn'))
    .otherwise(() => null);

  const handleOpenCoreExchange = () =>
    window.open(
      composePath(warehouseRoutes.coreExchangeDetailOverview, {
        params: {id: props.coreExchangeId},
        isSearchParamsPreserved: false,
      }),
      '_blank'
    );

  return (
    <HStack spacing={2} align="center">
      <Flag
        colorScheme="blue"
        label={i18n.t('entity.warehouse.labels.coreExchange')}
        data-testid={suffixTestId('coreExchangeFlag', props)}
      />
      <Show when={isNotNilOrEmpty(itemTypeLabel) ? itemTypeLabel : null}>
        {(label) => (
          <Flag
            colorScheme="blue"
            isSubtle
            label={label}
            data-testid={suffixTestId('coreExchangeItemTypeFlag', props)}
          />
        )}
      </Show>
      <Tooltip description={i18n.t('entity.warehouse.labels.coreExchange')}>
        <IconButton
          icon="action/open_in_new"
          size="small"
          onClick={handleOpenCoreExchange}
          aria-label={i18n.t('entity.warehouse.labels.coreExchange')}
          data-testid={suffixTestId('openCoreExchange', props)}
        />
      </Tooltip>
    </HStack>
  );
}
